import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useSelector } from "react-redux";
import { getContacts } from "../components/Contacts/actions/getContacts";

export const SocketContext = createContext(null);

const DASHBOARD_ROLES = new Set(["business", "admin", "virtualReceptionist", "accountManager", "other", "superadmin"]);
const WORKFORCE_ROLES = new Set(["employee", "workforce"]);

export const SocketProvider = ({ children }) => {
  const { uid: userUid, companies, workforces } = useSelector((state) => state.userState);

  const [socket, setSocket] = useState(null);

  const memberships = useMemo(
    () =>
      (companies ?? [])
        .map((membership) => ({
          companyId: membership?.companyId,
          roleType: membership?.userRole?.role?.type,
        }))
        .filter((membership) => membership.companyId != null),
    [companies],
  );

  const workforceMemberships = useMemo(
    () => (workforces ?? []).filter((workforce) => workforce?.companyId != null),
    [workforces],
  );

  const membershipsSignature = useMemo(
    () =>
      memberships
        .map(({ companyId, roleType }) => `${companyId}:${roleType ?? ""}`)
        .sort()
        .join("|"),
    [memberships],
  );

  const workforcesSignature = useMemo(
    () =>
      workforceMemberships
        .map((workforce) => `${workforce.companyId}:${workforce.uid ?? ""}`)
        .sort()
        .join("|"),
    [workforceMemberships],
  );

  const membershipsRef = useRef(memberships);
  membershipsRef.current = memberships;

  const workforcesRef = useRef(workforceMemberships);
  workforcesRef.current = workforceMemberships;

  const joinRooms = useCallback(
    (rooms) => {
      if (!socket || !rooms?.length) return;
      socket.emit("join", rooms);
    },
    [socket],
  );

  useEffect(() => {
    if (!userUid) return;

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const isLocalhost = apiUrl.includes("localhost");
    const socketUrl = apiUrl.replace("/flownect-api", "");
    const socketPath = isLocalhost ? "/socket.io/" : "/flownect-api/socket.io/";

    const socketInstance = io(socketUrl, {
      path: socketPath,
      transports: ["websocket"],
      reconnection: true,
    });

    const registerUser = () => {
      if (socketInstance.connected) socketInstance.emit("register", userUid);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") registerUser();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    socketInstance.on("connect", registerUser);

    setSocket(socketInstance);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      socketInstance.off("connect", registerUser);
      socketInstance.disconnect();
      setSocket(null);
    };
  }, [userUid]);

  useEffect(() => {
    if (!socket || !userUid) return;
    if (!membershipsSignature && !workforcesSignature) return;

    let cancelled = false;

    const buildRooms = async () => {
      const rooms = new Set();
      const companyIdsNeedingWorkforceSubrooms = [];

      membershipsRef.current.forEach(({ companyId, roleType }) => {
        rooms.add(`room_company_${companyId}`);

        if (DASHBOARD_ROLES.has(roleType)) companyIdsNeedingWorkforceSubrooms.push(companyId);
      });

      workforcesRef.current.forEach((workforce) => {
        rooms.add(`room_company_${workforce.companyId}`);
        if (workforce.uid) rooms.add(`room_company_${workforce.companyId}_workforce_${workforce.uid}`);
      });

      const isWorkforceUser =
        workforcesRef.current.length > 0 || membershipsRef.current.some(({ roleType }) => WORKFORCE_ROLES.has(roleType));

      if (isWorkforceUser) rooms.add(`room_workforce_${userUid}`);

      if (companyIdsNeedingWorkforceSubrooms.length) {
        const token = sessionStorage.getItem("token");

        const results = await Promise.allSettled(
          companyIdsNeedingWorkforceSubrooms.map(async (companyId) => ({
            companyId,
            contacts: await getContacts(token, companyId),
          })),
        );

        results.forEach((result) => {
          if (result.status !== "fulfilled") return;

          const { companyId, contacts } = result.value;
          (contacts?.items ?? []).forEach((contact) => {
            if (!contact?.uid) return;
            rooms.add(`room_company_${companyId}_workforce_${contact.uid}`);
          });
        });
      }

      return Array.from(rooms);
    };

    const joinAllRooms = async () => {
      try {
        const rooms = await buildRooms();
        if (cancelled || !socket.connected || !rooms.length) return;

        socket.emit("join", rooms);
        console.log(`[SOCKET] Joined ${rooms.length} rooms across ${membershipsRef.current.length} companies`);
      } catch (error) {
        console.error("[SOCKET] Error joining rooms:", error.message);
      }
    };

    if (socket.connected) void joinAllRooms();
    socket.on("connect", joinAllRooms);

    return () => {
      cancelled = true;
      socket.off("connect", joinAllRooms);
    };
  }, [socket, userUid, membershipsSignature, workforcesSignature]);

  return <SocketContext.Provider value={{ socket, joinRooms }}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
